/**
 * Map Tab Content for GameMenu
 * POE-style act tabs + zone list with travel buttons
 */

import { MenuTabContent } from '../MenuTabContent';
import { Player } from '@client/entities/Player';
import { ZONES, ZoneType } from '@shared/world/Zone';
import { getWaypointManager, WAYPOINT_ZONES } from '@shared/world/WaypointManager';
import { t } from '@locales/index';

// ---- Act data ----

interface ActDef {
  id: number;
  zones: ZoneType[];
}

const ACTS: ActDef[] = [
  { id: 1, zones: ['otag_koyu', 'karanlik_orman', 'ruh_batakligi', 'erlik_tapinagi'] },
  { id: 2, zones: ['ergenekon_girisi', 'firtina_ovasi', 'ates_magarasi', 'yersub_tahti'] },
  { id: 3, zones: ['karanlik_tunel', 'olum_nehri', 'kemik_ormani', 'lanetli_saray'] },
  { id: 4, zones: ['erlik_kapisi', 'cehennem_bahcesi', 'taht_odasi'] },
];

// ---- Biome colors (strip on left of each row) ----

type BiomeKey = 'forest' | 'swamp' | 'temple' | 'mountain' | 'fire' | 'underground' | 'hell' | 'village' | 'steppe';

const BIOME_COLORS: Record<BiomeKey, string> = {
  village: '#a89060',
  forest: '#2d8a27',
  swamp: '#3a7a5a',
  temple: '#8a4aaa',
  mountain: '#7a8a9a',
  steppe: '#8a9a4a',
  fire: '#cc5522',
  underground: '#7a4aaa',
  hell: '#cc3333',
};

function getZoneBiome(id: ZoneType): BiomeKey {
  switch (id) {
    case 'otag_koyu': return 'village';
    case 'karanlik_orman': return 'forest';
    case 'ruh_batakligi': return 'swamp';
    case 'erlik_tapinagi': return 'temple';
    case 'ergenekon_girisi': return 'mountain';
    case 'firtina_ovasi': return 'steppe';
    case 'ates_magarasi': return 'fire';
    case 'yersub_tahti': return 'underground';
    case 'karanlik_tunel': return 'underground';
    case 'olum_nehri': return 'swamp';
    case 'kemik_ormani': return 'forest';
    case 'lanetli_saray': return 'underground';
    case 'erlik_kapisi': return 'hell';
    case 'cehennem_bahcesi': return 'hell';
    case 'taht_odasi': return 'hell';
    default: return 'village';
  }
}

// ---- Hit rect helper ----

interface Rect { x: number; y: number; w: number; h: number }

function inside(mx: number, my: number, r: Rect): boolean {
  return mx >= r.x && mx <= r.x + r.w && my >= r.y && my <= r.y + r.h;
}

// ---- MapTab ----

export class MapTab extends MenuTabContent {
  private selectedAct = 0;
  private currentRegionId: string = 'otag_koyu';

  // Hover tracking
  private hoveredActIdx = -1;
  private hoveredZoneIdx = -1;
  private hoveredBtnIdx = -1;

  // Cached hit areas
  private actTabRects: Rect[] = [];
  private zoneRowRects: Rect[] = [];
  private zoneBtnRects: Rect[] = [];
  private zoneIds: ZoneType[] = [];

  public onTravel?: (regionId: string) => void;

  // Layout constants
  private readonly TAB_H = 36;
  private readonly ROW_H = 58;

  constructor(player: Player) {
    super(player);
  }

  setCurrentRegion(regionId: string): void {
    this.currentRegionId = regionId;
    // Auto-select act
    for (let i = 0; i < ACTS.length; i++) {
      if ((ACTS[i].zones as string[]).includes(regionId)) {
        this.selectedAct = i;
        break;
      }
    }
  }

  measureSize(_ctx: CanvasRenderingContext2D): { width: number; height: number } {
    return { width: 600, height: 400 };
  }

  update(_deltaTime: number): void {}

  onActivate(): void {
    this.hoveredActIdx = -1;
    this.hoveredZoneIdx = -1;
    this.hoveredBtnIdx = -1;
  }

  // ---- Render ----

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.bounds;
    const pad = 12;

    // Act tab bar
    this.renderActTabs(ctx, x + pad, y + 8, width - pad * 2);

    // Zone list
    const listY = y + 8 + this.TAB_H + 8;
    const listH = height - this.TAB_H - 40;
    this.renderZoneList(ctx, x + pad, listY, width - pad * 2, listH);

    // Footer hint
    ctx.fillStyle = '#504840';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(t('worldMap.instructions'), x + width / 2, y + height - 10);
  }

  // ---- Act Tabs ----

  private renderActTabs(ctx: CanvasRenderingContext2D, tx: number, ty: number, tw: number): void {
    this.actTabRects = [];
    const tabW = tw / ACTS.length;

    // Tab bar background
    ctx.fillStyle = '#0e0c0a';
    this.drawRoundedRect(ctx, tx, ty, tw, this.TAB_H, 6);
    ctx.fill();

    for (let i = 0; i < ACTS.length; i++) {
      const tabX = tx + i * tabW;
      const isSel = i === this.selectedAct;
      const isHov = i === this.hoveredActIdx;
      this.actTabRects.push({ x: tabX, y: ty, w: tabW, h: this.TAB_H });

      // Tab bg
      if (isSel) {
        ctx.fillStyle = '#221e18';
        ctx.fillRect(tabX + 1, ty + 1, tabW - 2, this.TAB_H - 2);
      } else if (isHov) {
        ctx.fillStyle = '#1a1610';
        ctx.fillRect(tabX + 1, ty + 1, tabW - 2, this.TAB_H - 2);
      }

      // Active gold bar
      if (isSel) {
        ctx.fillStyle = '#d4af37';
        ctx.fillRect(tabX + 10, ty + this.TAB_H - 3, tabW - 20, 3);
      }

      // Tab text
      ctx.fillStyle = isSel ? '#d4af37' : (isHov ? '#c0a050' : '#706050');
      ctx.font = 'bold 13px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(t('waypoint.actLabel', { num: ACTS[i].id }), tabX + tabW / 2, ty + this.TAB_H / 2);

      // Separator
      if (i < ACTS.length - 1) {
        ctx.strokeStyle = '#2a2520';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(tabX + tabW, ty + 6);
        ctx.lineTo(tabX + tabW, ty + this.TAB_H - 6);
        ctx.stroke();
      }
    }

    // Bottom line
    ctx.strokeStyle = '#3a3028';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(tx, ty + this.TAB_H);
    ctx.lineTo(tx + tw, ty + this.TAB_H);
    ctx.stroke();
  }

  // ---- Zone List ----

  private renderZoneList(ctx: CanvasRenderingContext2D, lx: number, ly: number, lw: number, lh: number): void {
    const act = ACTS[this.selectedAct];
    if (!act) return;

    this.zoneRowRects = [];
    this.zoneBtnRects = [];
    this.zoneIds = [];

    ctx.save();
    ctx.beginPath();
    ctx.rect(lx - 2, ly - 2, lw + 4, lh + 4);
    ctx.clip();

    for (let i = 0; i < act.zones.length; i++) {
      const zoneId = act.zones[i];
      const zone = ZONES[zoneId];
      const ry = ly + i * this.ROW_H;

      if (ry + this.ROW_H < ly || ry > ly + lh) continue;

      const isCurrent = zoneId === this.currentRegionId;
      const isHov = i === this.hoveredZoneIdx;
      const isBtnHov = i === this.hoveredBtnIdx;
      const isBoss = !!zone.bossType;
      const biomeColor = BIOME_COLORS[getZoneBiome(zoneId)];
      const wpInfo = WAYPOINT_ZONES[zoneId];
      const hasWP = wpInfo?.hasWaypoint ?? false;
      const wpActive = hasWP && getWaypointManager().isWaypointActivated(zoneId);

      const rowH = this.ROW_H - 4;
      const rowRect: Rect = { x: lx, y: ry, w: lw, h: rowH };
      this.zoneRowRects.push(rowRect);
      this.zoneIds.push(zoneId);

      // Row bg
      if (isCurrent) {
        ctx.fillStyle = '#172a14';
      } else if (isHov) {
        ctx.fillStyle = '#1e1a14';
      } else {
        ctx.fillStyle = (i % 2 === 0) ? '#141210' : '#121010';
      }
      this.drawRoundedRect(ctx, lx, ry, lw, rowH, 5);
      ctx.fill();

      // Row border
      if (isCurrent) {
        ctx.strokeStyle = '#3a8a3a';
        ctx.lineWidth = 2;
        this.drawRoundedRect(ctx, lx, ry, lw, rowH, 5);
        ctx.stroke();
      } else if (isHov) {
        ctx.strokeStyle = '#3a3228';
        ctx.lineWidth = 1;
        this.drawRoundedRect(ctx, lx, ry, lw, rowH, 5);
        ctx.stroke();
      }

      // Biome color strip (left)
      ctx.fillStyle = biomeColor;
      this.drawRoundedRect(ctx, lx, ry, 5, rowH, 5);
      ctx.fill();
      // Fix right side (overwrite rounded right corners)
      ctx.fillRect(lx + 3, ry, 3, rowH);

      // Waypoint icon
      const iconX = lx + 22;
      const iconY = ry + rowH / 2;
      if (hasWP) {
        if (wpActive) {
          ctx.fillStyle = '#44aaff';
          ctx.beginPath();
          ctx.arc(iconX, iconY, 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#aaddff';
          ctx.beginPath();
          ctx.moveTo(iconX, iconY - 3);
          ctx.lineTo(iconX + 2, iconY);
          ctx.lineTo(iconX, iconY + 3);
          ctx.lineTo(iconX - 2, iconY);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.strokeStyle = '#444';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(iconX, iconY, 5, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = '#555';
          ctx.font = '8px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('?', iconX, iconY + 1);
        }
      } else {
        ctx.strokeStyle = '#2a2520';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(iconX - 4, iconY);
        ctx.lineTo(iconX + 4, iconY);
        ctx.stroke();
      }

      // Zone name
      const textX = lx + 40;
      ctx.fillStyle = isCurrent ? '#5aee5a' : (isHov ? '#fff' : '#c0b8a0');
      ctx.font = `${isCurrent ? 'bold ' : ''}14px Arial`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const zoneName = t(`zones.${zoneId}`) || zone.name;
      ctx.fillText(zoneName, textX, ry + 18);

      // Level range + boss tag (second line)
      ctx.fillStyle = isCurrent ? '#6a9a6a' : '#706858';
      ctx.font = '11px Arial';
      let subText = t('waypoint.levelRange', { min: zone.levelRange.min, max: zone.levelRange.max });
      if (isBoss) subText += '  \u2620 ' + t('worldMap.bossZone');
      ctx.fillText(subText, textX, ry + 38);

      // Right side: current badge or travel button
      if (isCurrent) {
        const badgeW = 85;
        const badgeH = 22;
        const badgeX = lx + lw - badgeW - 10;
        const badgeY = ry + (rowH - badgeH) / 2;
        ctx.fillStyle = '#1a3a1a';
        this.drawRoundedRect(ctx, badgeX, badgeY, badgeW, badgeH, 4);
        ctx.fill();
        ctx.strokeStyle = '#3a8a3a';
        ctx.lineWidth = 1;
        this.drawRoundedRect(ctx, badgeX, badgeY, badgeW, badgeH, 4);
        ctx.stroke();
        ctx.fillStyle = '#5aee5a';
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(t('waypoint.currentZone'), badgeX + badgeW / 2, badgeY + badgeH / 2);

        this.zoneBtnRects.push({ x: 0, y: 0, w: 0, h: 0 }); // no btn
      } else {
        // Travel button
        const btnW = 85;
        const btnH = 26;
        const btnX = lx + lw - btnW - 10;
        const btnY = ry + (rowH - btnH) / 2;
        const btnRect: Rect = { x: btnX, y: btnY, w: btnW, h: btnH };
        this.zoneBtnRects.push(btnRect);

        ctx.fillStyle = isBtnHov ? '#2a6a2a' : '#1a3a1a';
        this.drawRoundedRect(ctx, btnX, btnY, btnW, btnH, 5);
        ctx.fill();
        ctx.strokeStyle = isBtnHov ? '#44cc44' : '#2a6a2a';
        ctx.lineWidth = 1.5;
        this.drawRoundedRect(ctx, btnX, btnY, btnW, btnH, 5);
        ctx.stroke();

        ctx.fillStyle = isBtnHov ? '#fff' : '#88ee88';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(t('worldMap.travelTo'), btnX + btnW / 2, btnY + btnH / 2);
      }
    }

    ctx.restore();
  }

  // ---- Mouse handling ----

  handleMouseEvent(e: import('../components/UIComponent').MouseEvent): boolean {
    if (!this.visible) return false;

    if (e.type === 'move') {
      // Act tabs
      this.hoveredActIdx = -1;
      for (let i = 0; i < this.actTabRects.length; i++) {
        if (inside(e.x, e.y, this.actTabRects[i])) { this.hoveredActIdx = i; break; }
      }

      // Zone rows
      this.hoveredZoneIdx = -1;
      this.hoveredBtnIdx = -1;
      for (let i = 0; i < this.zoneRowRects.length; i++) {
        if (inside(e.x, e.y, this.zoneRowRects[i])) this.hoveredZoneIdx = i;
        if (this.zoneBtnRects[i] && inside(e.x, e.y, this.zoneBtnRects[i])) this.hoveredBtnIdx = i;
      }
      return false;
    }

    if (e.type === 'down' && e.button === 'left') {
      // Act tab click
      for (let i = 0; i < this.actTabRects.length; i++) {
        if (inside(e.x, e.y, this.actTabRects[i])) {
          this.selectedAct = i;
          this.hoveredZoneIdx = -1;
          this.hoveredBtnIdx = -1;
          return true;
        }
      }

      // Travel button click
      for (let i = 0; i < this.zoneBtnRects.length; i++) {
        if (this.zoneBtnRects[i] && inside(e.x, e.y, this.zoneBtnRects[i])) {
          const zoneId = this.zoneIds[i];
          if (zoneId && zoneId !== this.currentRegionId && this.onTravel) {
            this.onTravel(zoneId);
            return true;
          }
        }
      }

      // Also allow clicking anywhere on the zone row to travel
      for (let i = 0; i < this.zoneRowRects.length; i++) {
        if (inside(e.x, e.y, this.zoneRowRects[i])) {
          const zoneId = this.zoneIds[i];
          if (zoneId && zoneId !== this.currentRegionId && this.onTravel) {
            this.onTravel(zoneId);
            return true;
          }
        }
      }
    }

    return false;
  }
}
